import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { getPaginationItems } from '@/utils/pagination';
import Table from '@/components/admin/Table';
import communityService, {
  MabarEventConfig,
  CustomFormField,
  CustomFormAnswerItem,
} from '@/services/communityService';
import CustomFormAnswersViewModal from '@/components/community/CustomFormAnswersViewModal';
import { mabarService } from '@/services/mabarService';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { isHttpUrl } from '@/utils/paymentUrl';
import { hostMabarStatistikPath, hostPaths } from '@/utils/hostPaths';
import { clientPaths } from '@/utils/clientPaths';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import DropdownButton, { DropdownItem } from '@/components/ui/DropdownButton';
import { toast } from 'react-hot-toast';
import showToast from '@/components/ui/Toast';
import Notification from '@/components/ui/Notification';
import { Badge } from '@/components/shadcn/badge';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/shadcn/card';
import {
  Table as ShadcnTable,
  TableHeader as ShadcnTableHeader,
  TableBody as ShadcnTableBody,
  TableRow as ShadcnTableRow,
  TableHead as ShadcnTableHead,
  TableCell as ShadcnTableCell,
} from '@/components/shadcn/table';
import {
  AlertDialog,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogHeader,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogContent,
  AlertDialogAction,
} from '@/components/shadcn/alert-dialog';
import PageSizeSelect from '@/components/ui/PageSizeSelect';
import SimpleSelect from '@/components/ui/SimpleSelect';
import {
  CANONICAL_BADMINTON_LEVELS,
  eventHasLevelTemplate,
  isBadmintonSport,
  readParticipantLevel,
  withUpdatedLevelAnswer,
} from '@/components/community/badminton/badmintonLevelUtils';
import LevelToggle from '@/components/community/badminton/LevelToggle';

// Available page size options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100] as const;

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
};

/** True only for real participant emails (skips empty / Sporto guest-system addresses). */
const hasDeliverableEmail = (email?: string | null) => {
  const value = (email || '').trim().toLowerCase();
  if (!value || !value.includes('@')) return false;
  const domain = value.slice(value.lastIndexOf('@') + 1);
  if (!domain) return false;
  if (domain === 'sporto.id') return false;
  if (domain.endsWith('.sporto.id')) return false;
  if (domain.endsWith('sporto.id') && domain.length > 'sporto.id'.length) {
    const prefix = domain.slice(0, -'sporto.id'.length);
    if (prefix.endsWith('-')) return false;
  }
  return true;
};

// Format date
const formatDate = (dateStr: string) => {
  try {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

// Types mirroring client dashboard types but via community API
interface DashboardParticipant {
  id: number;
  name: string;
  wa_number: string;
  email: string;
  payment_status: 'paid' | 'unpaid';
  payment_method: string;
  invoice_url: string;
  order_number?: string;
  price: number;
  role?: string;
  team?: string;
  status: string;
  mmr?: number;
  custom_form_answer?: CustomFormAnswerItem[];
}

interface DashboardPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

interface DashboardStats {
  filled_slots: number;
  total_slots: number;
  slot_percentage: number;
  profit: number;
  expenses: number;
  income: number;
  payment_fees?: number;
  paid_tx_count?: number;
}

interface RoleAvailability {
  role: string;
  total: number;
  filled: number;
  paid?: number;
  available: number;
  price?: number;
  team_member_counts?: Record<string, number>;
}

interface DashboardData {
  name: string;
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  lokasi_arena?: string;
  sport_category: { name: string; slug?: string };
  event_date: string;
  start_time: string;
  end_time: string;
  live_list_url?: string;
  participants: DashboardParticipant[];
  pagination: DashboardPagination;
  stats: DashboardStats;
  config: MabarEventConfig;
  roles_availability?: RoleAvailability[];
  custom_form?: CustomFormField[];
}

const SORT_KEY_TO_COLUMN: Record<string, string> = {
  mmr: 'mmr',
  name: 'name',
  role: 'role',
  team: 'team',
  wa_number: 'wa',
  email: 'email',
  payment_status: 'payment',
  status: 'status',
};

const MabarDashboardPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(1, parseInt(searchParams.get('page') || '1', 10))
  );
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
    const limit = parseInt(searchParams.get('limit') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(limit as (typeof PAGE_SIZE_OPTIONS)[number]) ? limit : 10;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const prevSearchTermRef = useRef('');
  const [sortKey, setSortKey] = useState<string | undefined>(() => {
    const by = searchParams.get('sort_by');
    return by ? SORT_KEY_TO_COLUMN[by] || by : undefined;
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(() => {
    const order = searchParams.get('sort_order');
    return order === 'asc' || order === 'desc' ? order : undefined;
  });

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState<DashboardParticipant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mobile Action Modals State
  const [selectedMobileParticipant, setSelectedMobileParticipant] = useState<DashboardParticipant | null>(null);
  const [isMobileActionsModalOpen, setIsMobileActionsModalOpen] = useState(false);
  const [isMobilePaymentStatusModalOpen, setIsMobilePaymentStatusModalOpen] = useState(false);
  const [isMobileMembershipStatusModalOpen, setIsMobileMembershipStatusModalOpen] = useState(false);
  const [viewingFormAnswersParticipant, setViewingFormAnswersParticipant] = useState<DashboardParticipant | null>(null);

  // Payment status change state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [participantToUpdate, setParticipantToUpdate] = useState<DashboardParticipant | null>(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState<'paid' | 'unpaid'>('paid');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [reviewPopupShow, setReviewPopupShow] = useState<boolean>(false);
  const [reviewParticipant, setReviewParticipant] = useState<DashboardParticipant | null>(null);

  // membership status change state
  const [isStatusMembershipModalOpen, setIsStatusMembershipModalOpen] = useState(false);
  const [participantMembershipToUpdate, setParticipantMembershipToUpdate] =
    useState<DashboardParticipant | null>(null);
  const [newMembershipStatus, setNewMembershipStatus] = useState<
    'pending' | 'confirmed' | 'canceled'
  >('pending');
  const [membershipStatusError, setMembershipStatusError] = useState<string | null>(null);

  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    type?: 'success' | 'error';
  }>({ isOpen: false, message: '', type: 'success' });

  // Payment reminder state
  const [isPaymentReminderBulkModalOpen, setIsPaymentReminderBulkModalOpen] = useState(false);
  const [isPaymentReminderSingleModalOpen, setIsPaymentReminderSingleModalOpen] = useState(false);
  const [participantForReminder, setParticipantForReminder] = useState<DashboardParticipant | null>(
    null
  );
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  // Add participant modal state
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [addParticipantTab, setAddParticipantTab] = useState<'data' | 'custom_form'>('data');
  const [addParticipantCustomAnswers, setAddParticipantCustomAnswers] = useState<Record<string, string>>({});
  const [addParticipantForm, setAddParticipantForm] = useState({
    name: '',
    role: '',
    team: '',
    wa_number: '',
    email: '',
  });
  const [isSubmittingAddParticipant, setIsSubmittingAddParticipant] = useState(false);

  // Edit participant modal state
  const [isEditParticipantModalOpen, setIsEditParticipantModalOpen] = useState(false);
  const [participantToEdit, setParticipantToEdit] = useState<DashboardParticipant | null>(null);
  const [editParticipantForm, setEditParticipantForm] = useState({
    name: '',
    role: '',
    team: '',
    wa_number: '',
    email: '',
  });
  const [isSubmittingEditParticipant, setIsSubmittingEditParticipant] = useState(false);

  // Map frontend column keys to backend sort_by values
  const COLUMN_TO_SORT_BY: Record<string, string> = {
    mmr: 'mmr',
    name: 'name',
    role: 'role',
    wa: 'wa_number',
    email: 'email',
    payment: 'payment_status',
    status: 'status',
  };

  const isInvasion = useMemo(() => {
    const slug = dashboard?.sport_category?.slug?.toLowerCase();
    return slug === 'sepak-bola' || slug === 'mini-soccer' || slug === 'futsal';
  }, [dashboard]);

  const hasCustomForm = useMemo(() => {
    return Boolean(dashboard?.custom_form && dashboard.custom_form.length > 0);
  }, [dashboard]);

  const isBadmintonLevelEvent = useMemo(() => {
    return (
      isBadmintonSport(dashboard?.sport_category?.slug) &&
      eventHasLevelTemplate(dashboard?.custom_form)
    );
  }, [dashboard]);

  // Load dashboard data
  const loadDashboard = async (
    page: number = 1,
    search: string = '',
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    limit?: number
  ) => {
    if (!slug) return;

    const perPage = limit ?? itemsPerPage;
    setLoading(true);
    try {
      const data = await communityService.getEventDashboard(
        slug,
        page,
        perPage,
        search,
        sortBy,
        sortOrder
      );
      setDashboard(data as unknown as DashboardData);
    } catch (error: any) {
      console.error('Failed to load dashboard:', error);
      toast.error(error.message || 'Gagal memuat data dashboard');
      navigate('/host/mabar');
    } finally {
      setLoading(false);
    }
  };

  // Debounce only for search. Page/sort changes load immediately so sort status persists when paginating.
  useEffect(() => {
    if (!slug) return;

    const searchChanged = searchTerm !== prevSearchTermRef.current;
    if (searchChanged) {
      const timer = setTimeout(() => {
        prevSearchTermRef.current = searchTerm;
        setCurrentPage(1);
        updateUrlParams(1, sortKey || undefined, sortOrder || undefined, itemsPerPage);
        loadDashboard(
          1,
          searchTerm,
          COLUMN_TO_SORT_BY[sortKey || ''] || undefined,
          sortOrder,
          itemsPerPage
        );
      }, 500);
      return () => clearTimeout(timer);
    }

    // Load immediately for page/sort/limit changes (no debounce) - keeps sort & limit active across pagination
    loadDashboard(
      currentPage,
      searchTerm,
      COLUMN_TO_SORT_BY[sortKey || ''] || undefined,
      sortOrder,
      itemsPerPage
    );
  }, [slug, currentPage, searchTerm, sortKey, sortOrder, itemsPerPage]);

  // No dropdown toggle logic needed with direct select

  // Sync sort, page & limit to URL so they persist across pagination
  const updateUrlParams = (
    page: number,
    sortKeyVal?: string,
    sortOrderVal?: 'asc' | 'desc',
    limitVal?: number
  ) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    params.set('limit', String(limitVal ?? itemsPerPage));
    if (sortKeyVal && sortOrderVal) {
      params.set('sort_by', COLUMN_TO_SORT_BY[sortKeyVal] || sortKeyVal);
      params.set('sort_order', sortOrderVal);
    } else {
      params.delete('sort_by');
      params.delete('sort_order');
    }
    setSearchParams(params, { replace: true });
  };

  // Handle page change - keep sort & limit in URL
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (dashboard?.pagination.total_pages || 1)) {
      setCurrentPage(page);
      updateUrlParams(page, sortKey || undefined, sortOrder || undefined, itemsPerPage);
    }
  };

  // Handle items per page change - reset to page 1
  const handleItemsPerPageChange = (value: (typeof PAGE_SIZE_OPTIONS)[number]) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    updateUrlParams(1, sortKey || undefined, sortOrder || undefined, value);
  };

  // Generate page numbers
  // Handle export PDF
  const handleExportPdf = async () => {
    if (!slug) return;

    try {
      toast.loading('Mengunduh PDF...');
      const blob = await communityService.exportEventPdf(slug);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mabar-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.dismiss();
      toast.success('PDF berhasil diunduh');
    } catch (error: any) {
      toast.dismiss();
      toast.error(error.message || 'Gagal mengunduh PDF');
    }
  };

  // Handle export XLSX
  const handleExportXlsx = async () => {
    if (!slug) return;

    try {
      toast.loading('Mengunduh Excel...');
      const blob = await communityService.exportEventXlsx(slug);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mabar-${slug}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.dismiss();
      toast.success('Excel berhasil diunduh');
    } catch (error: any) {
      toast.dismiss();
      toast.error(error.message || 'Gagal mengunduh Excel');
    }
  };


  // Handle delete participant
  const handleDeleteClick = (participant: DashboardParticipant) => {
    setParticipantToDelete(participant);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!slug || !participantToDelete) return;

    setIsDeleting(true);
    try {
      await communityService.deleteParticipant(slug, participantToDelete.id);
      toast.success('Participant berhasil dihapus');
      setIsDeleteModalOpen(false);
      setParticipantToDelete(null);
      // Reload data
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal menghapus participant');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle payment status change
  const handlePaymentStatusClick = (
    participant: DashboardParticipant,
    status: 'paid' | 'unpaid'
  ) => {
    setParticipantToUpdate(participant);
    setNewPaymentStatus(status);
    setIsStatusModalOpen(true);
  };

  const handleConfirmPaymentStatusChange = async () => {
    if (!slug || !participantToUpdate) return;

    setIsUpdatingStatus(true);
    try {
      await communityService.updatePaymentStatus(slug, participantToUpdate.id, newPaymentStatus);
      toast.success('Status pembayaran berhasil diubah');
      setIsStatusModalOpen(false);
      setParticipantToUpdate(null);
      // Reload data
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal mengubah status pembayaran');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleMembershipStatusClick = (
    participant: DashboardParticipant,
    status: 'pending' | 'confirmed' | 'canceled'
  ) => {
    setParticipantMembershipToUpdate(participant);
    setNewMembershipStatus(status);
    setMembershipStatusError(null);
    setIsStatusMembershipModalOpen(true);
  };

  const handleConfirmMembershipStatusChange = async () => {
    if (!slug || !participantMembershipToUpdate) return;

    setIsUpdatingStatus(true);
    setMembershipStatusError(null);
    try {
      await communityService.updateParticipantStatus(
        slug,
        participantMembershipToUpdate.id,
        newMembershipStatus
      );
      toast.success('Status member berhasil diubah');
      setIsStatusMembershipModalOpen(false);
      setParticipantMembershipToUpdate(null);
      setMembershipStatusError(null);
      // Reload data
      loadDashboard(currentPage);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Gagal mengubah status member';
      setMembershipStatusError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleTeamChange = async (participant: DashboardParticipant, newTeam: string) => {
    if (!slug || !participant || participant.team === newTeam) return;

    setIsUpdatingStatus(true);
    try {
      await communityService.updateParticipant(slug, participant.id, {
        team: newTeam,
      });
      toast.success(`Berhasil mengubah tim ke ${newTeam}`);
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(error?.message || 'Gagal mengubah tim peserta');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleLevelChange = async (participant: DashboardParticipant, newLevel: string) => {
    if (!slug || !participant) return;
    const current = readParticipantLevel({
      role: participant.role,
      answers: participant.custom_form_answer,
    });
    if (current === newLevel) return;

    setIsUpdatingStatus(true);
    try {
      await communityService.updateParticipantBadmintonLevel(slug, participant.id, newLevel);
      toast.success(`Berhasil mengubah level ke ${newLevel}`);
      setViewingFormAnswersParticipant((prev) => {
        if (!prev || prev.id !== participant.id) return prev;
        return {
          ...prev,
          custom_form_answer: withUpdatedLevelAnswer(
            prev.custom_form_answer,
            dashboard?.custom_form,
            newLevel
          ),
        };
      });
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Gagal mengubah level peserta'));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Loading state
  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Icon icon="mdi:alert-circle-outline" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Data tidak ditemukan</p>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    const url = `${window.location.origin}/event/${slug}`;
    navigator.clipboard.writeText(url);
    showToast.success('Link event berhasil disalin ke clipboard');
  };

  const handleDelete = async (slug: string) => {
    if (!slug) return;
    if (!window.confirm('Apakah Anda yakin ingin menghapus event ini?')) {
      return;
    }

    try {
      await communityService.deleteEvent(slug);
      toast.success('Event berhasil dihapus');
      navigate('/host');
    } catch (error) {
      console.error('Error deleting event:', error);
      setNotification({
        isOpen: true,
        message: 'Gagal menghapus event',
        type: 'error',
      });
    }
  };


  // Payment reminder handlers
  const handleOpenBulkPaymentReminderModal = async () => {
    if (!slug) return;
    try {
      const result = await communityService.getUnpaidParticipantsCount(slug);
      setUnpaidCount(result.count);
      if (result.count === 0) {
        toast.error('Tidak ada peserta yang perlu dikirimi reminder pembayaran');
        return;
      }
      setIsPaymentReminderBulkModalOpen(true);
    } catch (error: any) {
      toast.error(error.message || 'Gagal mendapatkan jumlah peserta unpaid');
    }
  };

  const headerItems: DropdownItem[] = [
    ...(dashboard?.type === 'paid'
      ? [
          {
            label: 'Kirim Reminder Pembayaran',
            icon: 'mdi:bell-ring-outline',
            onClick: handleOpenBulkPaymentReminderModal,
          },
        ]
      : []),
    {
      label: 'Kelola Game & Push MMR',
      icon: 'mdi:lightning-bolt',
      onClick: () =>
        navigate(
          hostMabarStatistikPath(
            slug || '',
            dashboard?.sport_category?.slug || dashboard?.sport_category?.name
          )
        ),
    },
    {
      label: 'Edit',
      icon: 'mynaui:edit-one',
      onClick: () => navigate(`/host/mabar/${slug}/edit`),
    },
    {
      label: 'Share Link Mabar',
      icon: 'mdi:share-variant-outline',
      onClick: () => handleShare(),
    },
    {
      label: 'Cancel Event',
      icon: 'mdi:close-circle-outline',
      onClick: () => handleDelete(slug || ''),
      destructive: true,
    },
  ];

  const handleSendBulkPaymentReminder = async () => {
    if (!slug) return;
    setIsSendingReminder(true);
    try {
      const result = await communityService.sendPaymentReminderBulk(slug);
      toast.success(`Berhasil mengirim ${result.emails_sent} reminder pembayaran`);
      setIsPaymentReminderBulkModalOpen(false);
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal mengirim reminder pembayaran');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleOpenSinglePaymentReminderModal = (participant: DashboardParticipant) => {
    setParticipantForReminder(participant);
    setIsPaymentReminderSingleModalOpen(true);
  };

  const handleOpenQrisPayment = async (participant: DashboardParticipant) => {
    const qrisLoadFallback = 'Gagal memuat pembayaran QRIS. Coba lagi beberapa saat.';
    const qrisPrepareFallback =
      'Gagal menyiapkan pembayaran QRIS. Pastikan dompet pembayaran komunitas aktif, lalu coba lagi.';

    const goToCheckout = (payload?: unknown, orderNumber?: string) => {
      const order = orderNumber || participant.order_number;
      if (!order) {
        toast.error('Data pembayaran QRIS belum tersedia untuk peserta ini');
        return;
      }
      navigate(clientPaths.event.checkout(order), {
        state: {
          paymentInstruction: payload,
          invoiceNumber: order,
          returnUrl: slug ? hostPaths.mabar.dashboard(slug) : hostPaths.mabar.list,
        },
      });
    };

    if (participant.order_number) {
      const toastId = toast.loading('Memuat halaman pembayaran QRIS...');
      try {
        const res = await mabarService.getTransactionCheckout(participant.order_number);
        toast.dismiss(toastId);
        if (res?.success) {
          if (res.payload) {
            goToCheckout(res.payload);
          } else if (isHttpUrl(res.invoice_url)) {
            void openExternalUrl(res.invoice_url);
          } else {
            goToCheckout();
          }
        } else {
          toast.error(res?.message || qrisLoadFallback);
        }
      } catch (err: unknown) {
        toast.dismiss(toastId);
        toast.error(getApiErrorMessage(err, qrisLoadFallback));
      }
      return;
    }

    if (isHttpUrl(participant.invoice_url)) {
      void openExternalUrl(participant.invoice_url);
      return;
    }

    // Peserta belum memiliki order_number (misalnya ditambahkan manual) -> generate QRIS on-demand
    if (slug) {
      const toastId = toast.loading('Menyiapkan pembayaran QRIS...');
      try {
        const res = await communityService.generateParticipantQRIS(slug, participant.id);
        toast.dismiss(toastId);
        if (res?.success && res.order_number) {
          loadDashboard(currentPage);
          if (res.payload) {
            goToCheckout(res.payload, res.order_number);
          } else if (isHttpUrl(res.invoice_url)) {
            void openExternalUrl(res.invoice_url);
          } else {
            goToCheckout(undefined, res.order_number);
          }
        } else {
          toast.error(res?.message || qrisPrepareFallback);
        }
      } catch (err: unknown) {
        toast.dismiss(toastId);
        toast.error(getApiErrorMessage(err, qrisPrepareFallback));
      }
      return;
    }

    toast.error('Data pembayaran QRIS belum tersedia untuk peserta ini');
  };

  const handleSendSinglePaymentReminder = async () => {
    if (!slug || !participantForReminder) return;
    setIsSendingReminder(true);
    try {
      await communityService.sendPaymentReminderSingle(slug, participantForReminder.id);
      toast.success(`Berhasil mengirim reminder ke ${participantForReminder.name}`);
      setIsPaymentReminderSingleModalOpen(false);
      setParticipantForReminder(null);
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal mengirim reminder pembayaran');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleOpenAddParticipantModal = () => {
    const defaultTeam = dashboard?.config?.is_sparring
      ? dashboard?.config?.teams?.[0]?.name || 'Tim A'
      : '';
    setAddParticipantForm({ name: '', role: '', team: defaultTeam, wa_number: '', email: '' });
    setAddParticipantCustomAnswers({});
    setAddParticipantTab('data');
    setIsAddParticipantModalOpen(true);
  };

  const handleCloseAddParticipantModal = () => {
    setIsAddParticipantModalOpen(false);
    setAddParticipantForm({ name: '', role: '', team: '', wa_number: '', email: '' });
    setAddParticipantCustomAnswers({});
    setAddParticipantTab('data');
  };

  const handleOpenEditParticipantModal = (participant: DashboardParticipant) => {
    setParticipantToEdit(participant);
    setEditParticipantForm({
      name: participant.name || '',
      role: participant.role || '',
      team: participant.team || '',
      wa_number: participant.wa_number || '',
      email: hasDeliverableEmail(participant.email) ? participant.email : '',
    });
    setIsEditParticipantModalOpen(true);
  };

  const handleCloseEditParticipantModal = () => {
    setIsEditParticipantModalOpen(false);
    setParticipantToEdit(null);
    setEditParticipantForm({ name: '', role: '', team: '', wa_number: '', email: '' });
  };

  const validateEditParticipantForm = (): boolean => {
    const { name, role, team, email } = editParticipantForm;
    if (!name.trim()) {
      toast.error('Nama member wajib diisi');
      return false;
    }
    if (dashboard?.config?.is_sparring && !team.trim()) {
      toast.error('Tim wajib dipilih untuk event Sparring');
      return false;
    }
    if (!role.trim()) {
      toast.error('Posisi wajib dipilih');
      return false;
    }
    const emailTrimmed = email.trim();
    if (emailTrimmed && (!emailTrimmed.includes('@') || !emailTrimmed.includes('.'))) {
      toast.error('Format email tidak valid');
      return false;
    }
    return true;
  };

  const handleEditParticipantSubmit = async () => {
    if (!slug || !participantToEdit) return;
    if (!validateEditParticipantForm()) return;

    setIsSubmittingEditParticipant(true);
    try {
      const { name, role, team, wa_number, email } = editParticipantForm;
      await communityService.updateParticipant(slug, participantToEdit.id, {
        name: name.trim(),
        role: role.trim(),
        team: team.trim() || undefined,
        email: email.trim() || undefined,
        wa_number: wa_number.trim() || undefined,
      });
      toast.success('Data member berhasil diupdate');
      handleCloseEditParticipantModal();
      loadDashboard(
        currentPage,
        searchTerm,
        COLUMN_TO_SORT_BY[sortKey || ''] || undefined,
        sortOrder,
        itemsPerPage
      );
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Gagal mengupdate data member'));
    } finally {
      setIsSubmittingEditParticipant(false);
    }
  };

  const validateParticipantDataStep = (): boolean => {
    const { name, role, team } = addParticipantForm;
    if (!name.trim()) {
      toast.error('Nama member wajib diisi');
      return false;
    }
    if (dashboard?.config?.is_sparring && !team.trim()) {
      toast.error('Tim wajib dipilih untuk event Sparring');
      return false;
    }
    if (!role.trim()) {
      toast.error('Role wajib dipilih');
      return false;
    }
    return true;
  };

  const handleNextToAddParticipantCustomForm = () => {
    if (!validateParticipantDataStep()) return;
    // Auto-prefill team / role / level template fields in custom form if available and empty
    if (dashboard?.custom_form && dashboard.custom_form.length > 0) {
      setAddParticipantCustomAnswers((prev) => {
        const updated = { ...prev };
        for (const field of dashboard.custom_form!) {
          if (!updated[field.id]) {
            const isTeamField =
              field.template_type === 'team' ||
              field.title.trim().toLowerCase() === 'tim';
            const isRoleField =
              field.template_type === 'form_role' ||
              field.template_type === 'role' ||
              field.title.trim().toLowerCase() === 'posisi' ||
              field.title.trim().toLowerCase() === 'role';

            if (isTeamField && addParticipantForm.team) {
              updated[field.id] = addParticipantForm.team;
            } else if (isRoleField && addParticipantForm.role) {
              updated[field.id] = addParticipantForm.role;
            }
          }
        }
        return updated;
      });
    }
    setAddParticipantTab('custom_form');
  };

  const handleAddParticipantSubmit = async () => {
    if (!slug) return;
    if (!validateParticipantDataStep()) {
      setAddParticipantTab('data');
      return;
    }

    let customFormAnswersPayload: CustomFormAnswerItem[] | undefined = undefined;

    if (hasCustomForm && dashboard?.custom_form && dashboard.custom_form.length > 0) {
      for (const field of dashboard.custom_form) {
        const val = (addParticipantCustomAnswers[field.id] || '').trim();
        if (field.required && !val) {
          toast.error(`Pertanyaan "${field.title}" wajib diisi`);
          setAddParticipantTab('custom_form');
          return;
        }
      }

      customFormAnswersPayload = dashboard.custom_form.map((field) => {
        const rawValue =
          addParticipantCustomAnswers[field.id] !== undefined
            ? addParticipantCustomAnswers[field.id]
            : '';
        return {
          field_id: field.id,
          question_id: field.id,
          title: field.title,
          type: field.type,
          template_type: field.template_type,
          value: String(rawValue ?? '').trim(),
        };
      });
    }

    setIsSubmittingAddParticipant(true);
    try {
      const { name, role, team, wa_number, email } = addParticipantForm;
      await communityService.addParticipant(slug, {
        name: name.trim(),
        role: role.trim(),
        team: team.trim() || undefined,
        email: email.trim() || undefined,
        wa_number: wa_number.trim() || undefined,
        status: 'confirmed',
        custom_form_answer:
          customFormAnswersPayload && customFormAnswersPayload.length > 0
            ? customFormAnswersPayload
            : undefined,
      });
      toast.success('Member berhasil ditambahkan');
      handleCloseAddParticipantModal();
      loadDashboard(currentPage);
    } catch (error: any) {
      toast.error(error.message || 'Gagal menambah member');
    } finally {
      setIsSubmittingAddParticipant(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto flex flex-col gap-3.5 sm:gap-5 pb-24 w-full max-w-full overflow-x-hidden">
      {/* Top Header Bar (Desktop Only) */}
      <div className="hidden md:flex items-center justify-between gap-3 bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/host/mabar')}
            className="w-9 h-9 rounded-xl bg-gray-50 border border-[#E0E6ED] flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <Icon icon="ph:arrow-left-bold" className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-base sm:text-lg font-extrabold text-[#0E1726]">Analitik Event</h1>
            <p className="text-xs text-gray-500 hidden sm:block">Ringkasan performa, keuangan, dan daftar peserta event</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DropdownButton
            label="Export"
            items={[
              { label: 'Export PDF View', icon: 'ph:file-pdf-bold', onClick: handleExportPdf },
              { label: 'Export Excel Member', icon: 'ph:file-xls-bold', onClick: handleExportXlsx },
            ]}
            triggerVariant="outline"
            triggerSize="sm"
            triggerClassName="border-[#FC480D] text-[#FC480D] hover:bg-[#FFF5EE] rounded-full px-4 py-1.5 text-xs font-bold"
          />
          <DropdownButton
            items={headerItems}
            triggerVariant="ghost"
            triggerSize="sm"
            triggerClassName="px-1 py-1"
          />
        </div>
      </div>

      {/* Event Details Card */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base sm:text-lg font-extrabold text-[#0E1726] leading-tight">
            {dashboard.name}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${
                dashboard.visibility === 'public'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-[#F3EDFF] text-[#7C3AED]'
              }`}
            >
              {dashboard.visibility === 'public' ? 'PUBLIC' : 'PRIVATE'}
            </span>
            {/* Mobile Actions Dropdown */}
            <div className="flex items-center gap-1 md:hidden">
              <DropdownButton
                label="Export"
                items={[
                  { label: 'Export PDF View', icon: 'ph:file-pdf-bold', onClick: handleExportPdf },
                  { label: 'Export Excel Member', icon: 'ph:file-xls-bold', onClick: handleExportXlsx },
                ]}
                triggerVariant="outline"
                triggerSize="sm"
                triggerClassName="border-[#FC480D] text-[#FC480D] hover:bg-[#FFF5EE] rounded-full px-3 py-1 text-[11px] font-bold"
              />
              <DropdownButton
                items={headerItems}
                triggerVariant="ghost"
                triggerSize="sm"
                triggerClassName="px-1 py-1"
              />
            </div>
          </div>
        </div>

        <p className="text-xs sm:text-sm font-medium text-[#8C8C8C]">
          {dashboard.lokasi_arena || 'Gor Senayan - Yogyakarta'}
        </p>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FFF5EE] text-[#FC480D] text-xs font-medium">
            <Icon icon="ph:soccer-ball" className="w-3.5 h-3.5" />
            {dashboard.sport_category.name}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#F5F6F7] text-[#1F1F1F] text-xs font-medium">
            <Icon icon="ph:calendar-dots" className="w-3.5 h-3.5" />
            {formatDate(dashboard.event_date)}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#F5F6F7] text-[#1F1F1F] text-xs font-medium">
            <Icon icon="ph:clock-afternoon" className="w-3.5 h-3.5" />
            {formatLocalEventTimeRange(dashboard.start_time, dashboard.end_time)}
          </span>
        </div>
      </div>

      {/* Total Slot Card */}
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-[#8C8C8C]">
            <Icon icon="ph:users-three" className="w-4 h-4 text-gray-500" />
            Total Slot
          </span>
          <span className="text-xs sm:text-sm font-bold text-[#FC480D]">
            {dashboard.stats.slot_percentage}% terisi
          </span>
        </div>
        <div className="text-2xl sm:text-3xl font-extrabold text-[#0E1726] tracking-tight">
          {dashboard.stats.filled_slots}/{dashboard.stats.total_slots}
        </div>
        <div className="h-2.5 rounded-full bg-[#F0F2F4] overflow-hidden">
          <div
            className="h-2.5 rounded-full bg-[#FC480D] transition-all duration-300"
            style={{ width: `${Math.min(100, dashboard.stats.slot_percentage)}%` }}
          />
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Profit */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E9FBEF] text-[#0A9E3C] flex items-center justify-center shrink-0">
            <Icon icon="ph:trend-up-bold" className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-[#8C8C8C]">Profit</div>
            <div className={`text-base sm:text-lg font-extrabold ${dashboard.stats.profit >= 0 ? 'text-[#0A9E3C]' : 'text-red-500'}`}>
              {formatCurrency(dashboard.stats.profit)}
            </div>
          </div>
          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full shrink-0 ${dashboard.stats.profit >= 0 ? 'bg-[#E9FBEF] text-[#0A9E3C]' : 'bg-red-50 text-red-600'}`}>
            {dashboard.stats.profit >= 0 ? 'Surplus' : 'Defisit'}
          </span>
        </div>

        {/* Income */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E9FBEF] text-[#0A9E3C] flex items-center justify-center shrink-0">
            <Icon icon="ph:arrow-circle-down-fill" className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-[#8C8C8C]">Income</div>
            <div className="text-base sm:text-lg font-extrabold text-[#0E1726]">
              {formatCurrency(dashboard.stats.income)}
            </div>
          </div>
          <div className="text-xs font-medium text-[#8C8C8C] text-right whitespace-nowrap shrink-0">
            {dashboard.participants.filter(p => p.payment_status === 'paid').length} pemain bayar
          </div>
        </div>

        {/* Expenses */}
        <div className="bg-white rounded-2xl p-4 border border-[#E0E6ED] shadow-2xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FDEDEE] text-[#E7515A] flex items-center justify-center shrink-0">
            <Icon icon="ph:receipt-fill" className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-[#8C8C8C]">Expenses</div>
            <div className="text-base sm:text-lg font-extrabold text-[#0E1726]">
              {formatCurrency(dashboard.stats.expenses)}
            </div>
          </div>
        </div>
      </div>

      {/* Member List Section */}
      <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-extrabold text-[#0E1726]">Daftar Member</h2>
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
                {dashboard.stats.filled_slots} dari {dashboard.stats.total_slots} slot
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleOpenAddParticipantModal}
              className="hidden sm:flex h-9 px-4 rounded-xl bg-[#FC480D] text-white text-xs font-bold items-center gap-1.5 hover:bg-[#E8490F] transition-colors cursor-pointer shrink-0"
            >
              <Icon icon="ph:user-plus-bold" className="w-4 h-4" />
              <span>Tambah Pemain</span>
            </button>
          </div>
        </div>

        {/* Controls Row: Search Input */}
        <div className="relative w-full">
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cari nama member..."
            className="w-full border border-[#E0E6ED] rounded-xl pl-9 pr-4 py-2.5 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors bg-white shadow-2xs"
          />
          <Icon
            icon="ph:magnifying-glass-bold"
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
        </div>

        {/* List Content */}
        <div className="relative pt-1">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/60 rounded-xl">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FC480D]"></div>
            </div>
          )}

          {dashboard.participants.length === 0 ? (
            <div className={`text-center py-12 ${loading ? 'opacity-20' : ''}`}>
              <Icon
                icon="ph:users-slash-bold"
                className="w-12 h-12 text-gray-300 mx-auto mb-3"
              />
              <h3 className="text-base font-bold text-gray-800 mb-1">Belum Ada Member</h3>
              <p className="text-xs text-gray-500">Belum ada peserta yang bergabung ke event ini.</p>
            </div>
          ) : (
            <div className={loading ? 'opacity-50 pointer-events-none' : ''}>
              {/* Mobile Card List View */}
              <div className="block md:hidden space-y-3">
                {dashboard.participants.map((p) => {
                  const isPaid = p.payment_status === 'paid';
                  const isWaitingLeave = p.status === 'waiting_leave';
                  const teamName = p.team || (dashboard.config?.is_sparring ? dashboard.config?.teams?.[0]?.name : undefined);
                  const isBlue = teamName?.toLowerCase().includes('biru') || teamName?.toLowerCase().includes('b');
                  const teamColor = isBlue ? '#3B7DDB' : '#FC480D';

                  return (
                    <div key={p.id} className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
                      <div className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#FFF5EE] text-[#FC480D] font-extrabold text-sm flex items-center justify-center shrink-0 border border-[#FBD9C7]">
                          {p.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-bold text-sm text-[#0E1726] truncate">{p.name}</h4>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedMobileParticipant(p);
                                setIsMobileActionsModalOpen(true);
                              }}
                              className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                              aria-label="Member actions"
                            >
                              <Icon icon="ph:dots-three-vertical-bold" className="w-5 h-5" />
                            </button>
                          </div>
                          {p.team && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: teamColor }} />
                              <span>{p.team || ''}</span>
                            </div>
                          )}
                          <p className="font-medium text-xs !mt-0">Role : {p.role || ''}</p>
                          {p.wa_number && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                              <Icon icon="ph:whatsapp-logo-bold" className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                              <span>{p.wa_number}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="px-4 py-2.5 bg-[#FAFBFC] border-t border-[#F0F2F4] flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${isPaid ? 'bg-[#E9FBEF] text-[#0A9E3C]' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                            {isPaid && p.payment_method?.toLowerCase() === 'qris' && (
                              <Icon icon="ph:check-circle-fill" className="w-3 h-3 text-[#0A9E3C]" />
                            )}
                            <span>{isPaid ? 'LUNAS' : 'BELUM BAYAR'}</span>
                          </span>
                          <span className="text-xs font-bold text-[#0E1726]">
                            {dashboard.type === 'paid' ? formatCurrency(p.price) : 'Free'}
                          </span>
                        </div>

                        {p.status === 'pending' ? (
                          <button
                            type="button"
                            onClick={() => handleMembershipStatusClick(p, 'confirmed')}
                            className="h-7 px-3.5 rounded-full bg-[#FC480D] text-white text-xs font-bold hover:bg-[#E8490F] transition-colors cursor-pointer"
                          >
                            Konfirmasi
                          </button>
                        ) : (
                          <span className="text-xs font-semibold text-gray-500 capitalize">
                            {p.status === 'confirmed'
                              ? 'Terkonfirmasi'
                              : p.status === 'waiting_list'
                                ? 'Waiting List'
                                : p.status === 'unpaid'
                                  ? 'Unpaid'
                                  : p.status === 'waiting_leave'
                                    ? 'Menunggu Persetujuan'
                                    : p.status}
                          </span>
                        )}
                      </div>

                      {/* Unlist Request Banner */}
                      {isWaitingLeave && (
                        <div className="px-4 py-2.5 bg-[#FFFBF3] border-t border-[#F0F2F4] flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#B77B14]">
                            <Icon icon="ph:hand-waving-fill" className="w-4 h-4 text-[#B77B14]" />
                            <span>Minta keluar dari event</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => {
                                setReviewParticipant(p);
                                setReviewPopupShow(true);
                                setParticipantToDelete(p);
                              }}
                              className="h-7 px-3 rounded-full bg-[#FC480D] text-white text-xs font-bold hover:bg-[#E8490F] cursor-pointer"
                            >
                              Setujui
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto max-w-full">
                <Table
                  columns={[
                    { key: 'name', title: 'Nama Member', width: 'w-48', sortable: true },
                    ...(isInvasion ? [{ key: 'mmr', title: 'MMR', width: 'w-24', sortable: true }] : []),
                    { key: 'role', title: 'Role', width: 'w-28', sortable: true },
                    ...(dashboard.config?.is_sparring
                      ? [{ key: 'team', title: 'Ganti Tim', width: 'w-36', sortable: true }]
                      : []),
                    { key: 'wa', title: 'Whatsapp', width: 'w-36', sortable: true },
                    { key: 'email', title: 'Email', width: 'w-44', sortable: true },
                    { key: 'amount', title: 'Nominal', width: 'w-28', sortable: true },
                    { key: 'payment', title: 'Status Pembayaran', width: 'w-36', sortable: true },
                    { key: 'status', title: 'Member Status', width: 'w-36', sortable: true },
                  ]}
                  data={dashboard.participants.map((p) => ({
                    id: p.id,
                    name: p.name,
                    mmr: p.mmr ?? 0,
                    role: p.role || '-',
                    team:
                      p.team ||
                      (dashboard.config?.is_sparring
                        ? dashboard.config?.teams?.[0]?.name || 'Tim A'
                        : '-'),
                    wa: p.wa_number,
                    email: p.email,
                    amount: dashboard.type === 'paid' ? formatCurrency(p.price) : 'Free',
                    payment: p.payment_status,
                    status: p.status,
                    raw: p,
                  }))}
                  renderCell={(row, key) => {
                    if (key === 'mmr' && isInvasion) {
                      const mmrVal = Math.round(row.raw.mmr ?? 0);
                      return (
                        <span className="inline-flex items-center gap-1 font-semibold text-gray-800 text-sm">
                          <span className="text-orange-500 font-bold">⚡</span>
                          {mmrVal} MMR
                        </span>
                      );
                    }

                    if (key === 'team' && dashboard.config?.is_sparring) {
                      const rawTeams =
                        dashboard.config?.teams && dashboard.config.teams.length > 0
                          ? dashboard.config.teams
                          : [
                              { name: 'Tim A', jersey_color: '#FC480D' },
                              { name: 'Tim B', jersey_color: '#3B7DDB' },
                            ];

                      const teams = rawTeams.map((t, idx) => {
                        const color = t.jersey_color || (idx === 0 ? '#FC480D' : '#3B7DDB');
                        return {
                          value: t.name,
                          label: (
                            <div className="flex items-center gap-1.5 min-w-0 max-w-[120px] truncate">
                              <span
                                className="w-2.5 h-2.5 rounded-full border border-black/10 shrink-0"
                                style={{ backgroundColor: color }}
                              />
                              <span className="truncate font-medium text-gray-800">{t.name}</span>
                            </div>
                          ),
                        };
                      });

                      const currentTeam = row.raw.team || rawTeams[0]?.name || 'Tim A';

                      return (
                        <div className="inline-flex items-center gap-2 max-w-[150px] min-w-0">
                          <SimpleSelect
                            value={currentTeam}
                            onValueChange={(nextValue) => handleTeamChange(row.raw, nextValue)}
                            options={teams}
                            triggerClassName="h-8 px-2 text-xs bg-white w-36 max-w-[140px] [&>span]:min-w-0 [&>span]:truncate"
                          />
                        </div>
                      );
                    }

                    if (key === 'name') {
                      const teamName =
                        row.raw.team ||
                        (dashboard.config?.is_sparring
                          ? dashboard.config?.teams?.[0]?.name
                          : undefined);
                      const teamObj = dashboard.config?.teams?.find(
                        (t) => t.name.toLowerCase() === teamName?.toLowerCase()
                      );
                      const jerseyColor =
                        teamObj?.jersey_color ||
                        (teamName === dashboard.config?.teams?.[0]?.name ? '#FC480D' : '#3B7DDB');

                      return (
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            <span>{row.name}</span>
                            {row.status === 'waiting_leave' && (
                              <Badge variant={'destructive'} className="ml-2">
                                Request Leave
                              </Badge>
                            )}
                          </div>
                          {dashboard.config?.is_sparring && teamName && (
                            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                              <span
                                className="w-2 h-2 rounded-full border border-black/10 shrink-0"
                                style={{ backgroundColor: jerseyColor }}
                              />
                              <span className="font-semibold text-gray-700">{teamName}</span>
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (key === 'amount' && dashboard.type === 'paid') {
                      const isUnpaid = row.payment === 'unpaid';
                      return (
                        <div>
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(row.raw.price)}
                          </div>
                          {isUnpaid && (
                            <div className="text-[10px] leading-tight text-amber-600 font-medium mt-0.5">
                              *Disesuaikan harga event terbaru
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (key === 'payment' && dashboard.type === 'paid') {
                      const status = row.payment as 'paid' | 'unpaid';
                      const isQrisPaid =
                        row.raw.payment_method?.toLowerCase() === 'qris' && status === 'paid';
                      if (isQrisPaid) {
                        return (
                          <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full uppercase">
                            Paid
                            <Icon
                              icon="material-symbols:check-circle-rounded"
                              className="w-4 h-4 text-emerald-600"
                            />
                          </span>
                        );
                      }
                      return (
                        <div className="inline-flex items-center gap-2">
                          <SimpleSelect
                            value={status}
                            onValueChange={(nextValue) =>
                              handlePaymentStatusClick(
                                row.raw,
                                nextValue === 'paid' ? 'paid' : 'unpaid'
                              )
                            }
                            options={[
                              { value: 'paid', label: 'PAID' },
                              { value: 'unpaid', label: 'UNPAID' },
                            ]}
                            triggerClassName="h-8 px-2 text-sm bg-white"
                          />
                        </div>
                      );
                    } else if (key === 'payment') {
                      return <div className="inline-flex items-center gap-2">Free Event</div>;
                    }

                    if (key === 'status') {
                      const status = row.status;

                      if (row.status === 'waiting_leave') {
                        return (
                          <>
                            <ShadcnButton
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setReviewParticipant(row.raw);
                                setReviewPopupShow(true);
                                setParticipantToDelete(row.raw);
                              }}
                              disabled={isUpdatingStatus}
                            >
                              Review Cancel
                            </ShadcnButton>
                          </>
                        );
                      }

                      if (row.status === 'waiting_list') {
                        return (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                            Waiting List
                          </span>
                        );
                      }

                      if (row.status === 'unpaid') {
                        return (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                            Unpaid
                          </span>
                        );
                      }

                      return (
                        <>
                          <div className="inline-flex items-center gap-2">
                            <SimpleSelect
                              value={status}
                              onValueChange={(nextValue) =>
                                handleMembershipStatusClick(
                                  row.raw,
                                  nextValue as 'pending' | 'confirmed' | 'canceled'
                                )
                              }
                              options={[
                                { value: 'pending', label: 'Pending' },
                                { value: 'confirmed', label: 'Konfirmasi' },
                                { value: 'canceled', label: 'Tolak' },
                              ]}
                              triggerClassName="h-8 px-2 text-sm bg-white"
                            />
                          </div>
                        </>
                      );
                    }

                    return row[key];
                  }}
                  renderActions={(row) => {
                    const items: DropdownItem[] = [];

                    if (row.raw.custom_form_answer?.length || dashboard.custom_form?.length) {
                      items.push({
                        label: 'Lihat Jawaban Formulir',
                        icon: 'mdi:clipboard-text-outline',
                        onClick: () => setViewingFormAnswersParticipant(row.raw),
                      });
                    }

                    if (dashboard.type === 'paid' && row.payment === 'unpaid') {
                      items.push({
                        label: 'Bayar QRIS',
                        icon: 'mdi:qrcode-scan',
                        onClick: () => handleOpenQrisPayment(row.raw),
                      });

                      if (hasDeliverableEmail(row.raw.email)) {
                        items.push({
                          label: 'Kirim Reminder Bayar',
                          icon: 'mdi:bell-ring-outline',
                          onClick: () => handleOpenSinglePaymentReminderModal(row.raw),
                        });
                      }
                    }

                    if (row.raw.status !== 'canceled') {
                      items.push({
                        label: 'Edit Member',
                        icon: 'mdi:account-edit-outline',
                        onClick: () => handleOpenEditParticipantModal(row.raw),
                      });
                    }

                    items.push({
                      label: 'Delete',
                      icon: 'mdi:delete-outline',
                      onClick: () => handleDeleteClick(row.raw),
                      destructive: true,
                    });

                    return (
                      <DropdownButton
                        items={items}
                        triggerVariant="ghost"
                        triggerSize="sm"
                        triggerClassName="px-1 py-1"
                      />
                    );
                  }}
                  sortingMode="remote"
                  sortKey={sortKey}
                  sortOrder={sortOrder}
                  onSort={(key, order) => {
                    setSortKey(key);
                    setSortOrder(order);
                    setCurrentPage(1);
                    updateUrlParams(1, key, order, itemsPerPage);
                  }}
                />
              </div>

              {/* Smart Pagination Controls */}
              {dashboard.pagination.total_pages > 1 && (
                <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[#F0F2F4] mt-4">
                  <span className="text-xs text-gray-500 font-medium">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, dashboard.pagination.total)} of{' '}
                    {dashboard.pagination.total} entries
                  </span>

                  <div className="flex items-center gap-1.5 flex-wrap justify-center">
                    <PageSizeSelect
                      value={itemsPerPage}
                      options={PAGE_SIZE_OPTIONS}
                      onValueChange={(size) => {
                        if (PAGE_SIZE_OPTIONS.includes(size as (typeof PAGE_SIZE_OPTIONS)[number])) {
                          handleItemsPerPageChange(size as (typeof PAGE_SIZE_OPTIONS)[number]);
                        }
                      }}
                      triggerClassName="mr-2 bg-white h-8 text-xs"
                    />

                    <button
                      disabled={currentPage <= 1 || loading}
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
                    >
                      <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
                    </button>

                    {getPaginationItems(currentPage, dashboard.pagination.total_pages).map((item, idx) => {
                      if (item === '...') {
                        return (
                          <span
                            key={`ellipsis-${idx}`}
                            className="w-6 h-8 flex items-center justify-center text-xs text-gray-400 font-bold select-none shrink-0"
                          >
                            ...
                          </span>
                        );
                      }

                      const pNum = item as number;
                      return (
                        <button
                          key={pNum}
                          onClick={() => handlePageChange(pNum)}
                          className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all cursor-pointer shrink-0 ${
                            currentPage === pNum
                              ? 'bg-[#FC480D] text-white shadow-2xs'
                              : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pNum}
                        </button>
                      );
                    })}

                    <button
                      disabled={currentPage >= dashboard.pagination.total_pages || loading}
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white flex items-center justify-center text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors cursor-pointer shrink-0"
                    >
                      <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ➕ FLOATING ACTION BUTTON (FAB) FOR MOBILE */}
      <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 z-40 md:hidden">
        <button
          type="button"
          onClick={handleOpenAddParticipantModal}
          className="h-12 px-5 rounded-full bg-gradient-to-r from-[#FF8552] to-[#FC480D] text-white text-sm font-bold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all cursor-pointer active:scale-95"
        >
          <Icon icon="ph:user-plus-bold" className="w-4 h-4 md:w-5 md:h-5" />
          <span>Tambah Pemain</span>
        </button>
      </div>

      {/* Realisasi Income & Realisasi Expenses Section */}
      {dashboard && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0">
          {/* Card 1: Realisasi Income */}
          <Card className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-sm">
            <CardHeader className="p-0 mb-4 pb-3 border-b border-gray-100 flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                  <Icon icon="mdi:cash-plus" className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="font-bold text-gray-900 text-base">
                    Realisasi Income
                  </CardTitle>
                </div>
              </div>
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200 font-bold px-2.5 py-1 text-xs sm:text-sm"
              >
                {formatCurrency(dashboard.stats.income || 0)}
              </Badge>
            </CardHeader>

            <CardContent className="p-0">
              {/* Desktop View (Table) */}
              <div className="hidden sm:block border border-gray-200 rounded-lg overflow-hidden bg-white">
                <ShadcnTable className="w-full text-sm">
                  <ShadcnTableHeader className="bg-gray-50">
                    <ShadcnTableRow>
                      <ShadcnTableHead className="px-4 py-2.5 text-left font-medium text-gray-600">
                        Posisi Pemain
                      </ShadcnTableHead>
                      <ShadcnTableHead className="px-4 py-2.5 text-center font-medium text-gray-600">
                        Sudah Bayar
                      </ShadcnTableHead>
                      <ShadcnTableHead className="px-4 py-2.5 text-right font-medium text-gray-600">
                        Biaya/Orang
                      </ShadcnTableHead>
                      <ShadcnTableHead className="px-4 py-2.5 text-right font-medium text-gray-600">
                        Subtotal
                      </ShadcnTableHead>
                    </ShadcnTableRow>
                  </ShadcnTableHeader>
                  <ShadcnTableBody>
                    {dashboard.roles_availability && dashboard.roles_availability.length > 0 ? (
                      dashboard.roles_availability.map((role, idx) => {
                        const paidCount = role.paid || 0;
                        const price = role.price || 0;
                        const subtotal = paidCount * price;
                        return (
                          <ShadcnTableRow key={idx} className="border-t border-gray-100">
                            <ShadcnTableCell className="px-4 py-2.5 text-gray-800 font-medium">
                              {role.role}
                            </ShadcnTableCell>
                            <ShadcnTableCell className="px-4 py-2.5 text-center text-gray-800">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {paidCount} / {role.total}
                              </span>
                            </ShadcnTableCell>
                            <ShadcnTableCell className="px-4 py-2.5 text-right text-gray-800">
                              {dashboard.type === 'free' || price === 0
                                ? 'Free'
                                : formatCurrency(price)}
                            </ShadcnTableCell>
                            <ShadcnTableCell className="px-4 py-2.5 text-right font-semibold text-gray-900">
                              {dashboard.type === 'free' || price === 0
                                ? 'Rp. 0'
                                : formatCurrency(subtotal)}
                            </ShadcnTableCell>
                          </ShadcnTableRow>
                        );
                      })
                    ) : (
                      <ShadcnTableRow className="border-t border-gray-100">
                        <ShadcnTableCell
                          colSpan={4}
                          className="px-4 py-3 text-center text-gray-500 text-xs"
                        >
                          Tidak ada posisi pemain
                        </ShadcnTableCell>
                      </ShadcnTableRow>
                    )}
                    <ShadcnTableRow className="border-t border-gray-200 bg-gray-50 font-bold">
                      <ShadcnTableCell className="px-4 py-2.5 text-gray-900">
                        Total Income
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-center text-gray-900">
                        {(dashboard.roles_availability || []).reduce(
                          (sum, r) => sum + (r.paid || 0),
                          0
                        )}{' '}
                        Pemain
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-right text-gray-500">
                        -
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-right text-emerald-600">
                        {formatCurrency(dashboard.stats.income || 0)}
                      </ShadcnTableCell>
                    </ShadcnTableRow>
                  </ShadcnTableBody>
                </ShadcnTable>
              </div>

              {/* Mobile View (Stacked Cards) */}
              <div className="block sm:hidden space-y-3">
                {dashboard.roles_availability && dashboard.roles_availability.length > 0 ? (
                  dashboard.roles_availability.map((role, idx) => {
                    const paidCount = role.paid || 0;
                    const price = role.price || 0;
                    const subtotal = paidCount * price;
                    return (
                      <Card
                        key={idx}
                        className="p-3 bg-gray-50/70 border-gray-200 shadow-none space-y-2 text-xs"
                      >
                        <div className="flex justify-between items-center pb-1.5 border-b border-gray-200/60">
                          <span className="font-bold text-gray-900 text-sm">{role.role}</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Sudah Bayar: {paidCount} / {role.total}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-gray-600">
                          <span>Biaya / Orang</span>
                          <span className="font-medium text-gray-800">
                            {dashboard.type === 'free' || price === 0
                              ? 'Free'
                              : formatCurrency(price)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                          <span className="font-semibold text-gray-700">Subtotal Realisasi</span>
                          <span className="font-bold text-emerald-700 text-sm">
                            {dashboard.type === 'free' || price === 0
                              ? 'Rp. 0'
                              : formatCurrency(subtotal)}
                          </span>
                        </div>
                      </Card>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-gray-400 text-xs italic">
                    Tidak ada posisi pemain
                  </div>
                )}

                {/* Mobile Total Income Footer Card */}
                <Card className="p-3.5 bg-emerald-50/80 border-emerald-200 shadow-none flex flex-row justify-between items-center text-xs">
                  <div>
                    <span className="font-bold text-emerald-950 text-sm block">
                      Total Realisasi Income
                    </span>
                    <span className="text-emerald-700 font-medium">
                      {(dashboard.roles_availability || []).reduce(
                        (sum, r) => sum + (r.paid || 0),
                        0
                      )}{' '}
                      Pemain
                    </span>
                  </div>
                  <span className="font-bold text-emerald-700 text-base">
                    {formatCurrency(dashboard.stats.income || 0)}
                  </span>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Realisasi Expenses */}
          <Card className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-sm">
            <CardHeader className="p-0 mb-4 pb-3 border-b border-gray-100 flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-rose-50 rounded-lg text-rose-600">
                  <Icon icon="mdi:receipt-text-minus-outline" className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="font-bold text-gray-900 text-base">
                    Realisasi Expenses
                  </CardTitle>
                </div>
              </div>
              <Badge
                variant="outline"
                className="bg-rose-50 text-rose-700 border-rose-200 font-bold px-2.5 py-1 text-xs sm:text-sm"
              >
                {formatCurrency(dashboard.stats.expenses || 0)}
              </Badge>
            </CardHeader>

            <CardContent className="p-0">
              {/* Desktop View (Table) */}
              <div className="hidden sm:block border border-gray-200 rounded-lg overflow-hidden bg-white">
                <ShadcnTable className="w-full text-sm">
                  <ShadcnTableHeader className="bg-gray-50">
                    <ShadcnTableRow>
                      <ShadcnTableHead className="px-4 py-2.5 text-left font-medium text-gray-600">
                        Jenis Operasional
                      </ShadcnTableHead>
                      <ShadcnTableHead className="px-4 py-2.5 text-center font-medium text-gray-600">
                        Jumlah
                      </ShadcnTableHead>
                      <ShadcnTableHead className="px-4 py-2.5 text-right font-medium text-gray-600">
                        Biaya
                      </ShadcnTableHead>
                    </ShadcnTableRow>
                  </ShadcnTableHeader>
                  <ShadcnTableBody>
                    {/* List Operational Costs */}
                    {(dashboard.config?.operational_costs || []).length > 0 ? (
                      (dashboard.config.operational_costs || []).map((cost: any, idx: number) => {
                        const desc =
                          cost.deskripsi ||
                          cost.name ||
                          cost.description ||
                          `Operasional #${idx + 1}`;
                        const qty = cost.jumlahItem ?? cost.quantity ?? 1;
                        const amount = cost.biaya ?? cost.amount ?? 0;
                        return (
                          <ShadcnTableRow key={idx} className="border-t border-gray-100">
                            <ShadcnTableCell className="px-4 py-2.5 text-gray-800">
                              {desc}
                            </ShadcnTableCell>
                            <ShadcnTableCell className="px-4 py-2.5 text-center text-gray-800">
                              {qty}
                            </ShadcnTableCell>
                            <ShadcnTableCell className="px-4 py-2.5 text-right text-gray-800">
                              {formatCurrency(amount)}
                            </ShadcnTableCell>
                          </ShadcnTableRow>
                        );
                      })
                    ) : (
                      <ShadcnTableRow className="border-t border-gray-100">
                        <ShadcnTableCell
                          colSpan={3}
                          className="px-4 py-3 text-center text-gray-400 text-xs italic"
                        >
                          Belum ada biaya operasional yang ditambahkan
                        </ShadcnTableCell>
                      </ShadcnTableRow>
                    )}

                    {/* Biaya Transaksi */}
                    <ShadcnTableRow className="border-t border-gray-100 bg-amber-50/50">
                      <ShadcnTableCell className="px-4 py-2.5 text-amber-900 font-medium">
                        <div className="flex items-center gap-1.5">
                          <Icon icon="mdi:credit-card-outline" className="w-4 h-4 text-amber-600" />
                          Biaya Transaksi
                        </div>
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-center text-amber-900 text-xs font-semibold">
                        {dashboard.stats.paid_tx_count || 0} Trx
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-right font-semibold text-amber-700">
                        {formatCurrency(dashboard.stats.payment_fees || 0)}
                      </ShadcnTableCell>
                    </ShadcnTableRow>

                    {/* Total Expenses */}
                    <ShadcnTableRow className="border-t border-gray-200 bg-gray-50 font-bold">
                      <ShadcnTableCell className="px-4 py-2.5 text-gray-900">
                        Total Expenses
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-center text-gray-500">
                        -
                      </ShadcnTableCell>
                      <ShadcnTableCell className="px-4 py-2.5 text-right text-rose-600">
                        {formatCurrency(dashboard.stats.expenses || 0)}
                      </ShadcnTableCell>
                    </ShadcnTableRow>
                  </ShadcnTableBody>
                </ShadcnTable>
              </div>

              {/* Mobile View (Stacked Cards) */}
              <div className="block sm:hidden space-y-3">
                {/* List Operational Costs Cards */}
                {(dashboard.config?.operational_costs || []).length > 0 ? (
                  (dashboard.config.operational_costs || []).map((cost: any, idx: number) => {
                    const desc =
                      cost.deskripsi || cost.name || cost.description || `Operasional #${idx + 1}`;
                    const qty = cost.jumlahItem ?? cost.quantity ?? 1;
                    const amount = cost.biaya ?? cost.amount ?? 0;
                    return (
                      <Card
                        key={idx}
                        className="p-3 bg-gray-50/70 border-gray-200 shadow-none flex flex-row justify-between items-center text-xs"
                      >
                        <div>
                          <span className="font-semibold text-gray-900 block">{desc}</span>
                          <span className="text-gray-500">Jumlah: {qty}</span>
                        </div>
                        <span className="font-bold text-gray-800 text-sm">
                          {formatCurrency(amount)}
                        </span>
                      </Card>
                    );
                  })
                ) : (
                  <div className="text-center py-2 text-gray-400 text-xs italic">
                    Belum ada biaya operasional
                  </div>
                )}

                {/* Payment Fee Card */}
                <Card className="p-3 bg-amber-50/80 border-amber-200 shadow-none flex flex-row justify-between items-center text-xs">
                  <div>
                    <div className="flex items-center gap-1 font-semibold text-amber-950">
                      <Icon icon="mdi:credit-card-outline" className="w-3.5 h-3.5 text-amber-600" />
                      Payment Fee Komunitas
                    </div>
                    <span className="text-amber-700 text-[11px]">
                      {dashboard.stats.paid_tx_count || 0} Transaksi Berhasil
                    </span>
                  </div>
                  <span className="font-bold text-amber-800 text-sm">
                    {formatCurrency(dashboard.stats.payment_fees || 0)}
                  </span>
                </Card>

                {/* Mobile Total Expenses Footer Card */}
                <Card className="p-3.5 bg-rose-50/80 border-rose-200 shadow-none flex flex-row justify-between items-center text-xs">
                  <div>
                    <span className="font-bold text-rose-950 text-sm block">
                      Total Realisasi Expenses
                    </span>
                    <span className="text-rose-700 font-medium">Operasional + Payment Fee</span>
                  </div>
                  <span className="font-bold text-rose-700 text-base">
                    {formatCurrency(dashboard.stats.expenses || 0)}
                  </span>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Konfirmasi Hapus"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin ingin menghapus <strong>{participantToDelete?.name}</strong> dari
            event ini?
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleConfirmDelete}
              loading={isDeleting}
              borderClass="border-red-600"
              textClass="text-red-600"
              hoverBorderClass="hover:border-red-700"
              hoverBgClass="hover:bg-red-50"
            >
              Ya, Hapus
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Participant Modal (Tambah Pemain) */}
      <Modal
        isOpen={isAddParticipantModalOpen}
        onClose={handleCloseAddParticipantModal}
        title="Tambah Pemain"
        subtitle={
          hasCustomForm
            ? addParticipantTab === 'data'
              ? 'Langkah 1 dari 2: Lengkapi data wajib pemain.'
              : 'Langkah 2 dari 2: Lengkapi formulir tambahan dari event ini.'
            : 'Daftarkan peserta secara manual ke event ini.'
        }
        size="md"
        position="responsive"
      >
        <div className="pt-1">
          {/* Tab Switcher if hasCustomForm */}
          {hasCustomForm && (
            <div className="flex items-center p-1 bg-gray-100 rounded-xl mb-4">
              <button
                type="button"
                onClick={() => setAddParticipantTab('data')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  addParticipantTab === 'data'
                    ? 'bg-white text-[#FC480D] shadow-2xs'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Icon icon="ph:user-bold" className="w-3.5 h-3.5" />
                <span>1. Data Wajib</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (validateParticipantDataStep()) {
                    handleNextToAddParticipantCustomForm();
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  addParticipantTab === 'custom_form'
                    ? 'bg-white text-[#FC480D] shadow-2xs'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <Icon icon="ph:clipboard-text-bold" className="w-3.5 h-3.5" />
                <span>2. Custom Form</span>
                {dashboard?.custom_form && dashboard.custom_form.some((f) => f.required) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FC480D]" />
                )}
              </button>
            </div>
          )}

          {/* TAB 1: DATA WAJIB */}
          {addParticipantTab === 'data' && (
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="add-participant-name"
                  className="block text-xs font-bold text-[#0E1726] mb-1.5"
                >
                  Nama <span className="text-[#FC480D]">*</span>
                </label>
                <input
                  id="add-participant-name"
                  type="text"
                  value={addParticipantForm.name}
                  onChange={(e) => setAddParticipantForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Nama lengkap pemain"
                  className="flex h-11 w-full rounded-2xl border border-[#E0E6ED] bg-white px-3.5 py-2 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="add-participant-email"
                    className="block text-xs font-bold text-[#0E1726] mb-1.5"
                  >
                    Email <span className="text-gray-400 font-normal">(opsional)</span>
                  </label>
                  <input
                    id="add-participant-email"
                    type="email"
                    value={addParticipantForm.email}
                    onChange={(e) =>
                      setAddParticipantForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="nama@email.com"
                    className="flex h-11 w-full rounded-2xl border border-[#E0E6ED] bg-white px-3.5 py-2 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs"
                  />
                </div>

                <div>
                  <label
                    htmlFor="add-participant-wa"
                    className="block text-xs font-bold text-[#0E1726] mb-1.5"
                  >
                    WhatsApp <span className="text-gray-400 font-normal">(opsional)</span>
                  </label>
                  <input
                    id="add-participant-wa"
                    type="text"
                    value={addParticipantForm.wa_number}
                    onChange={(e) =>
                      setAddParticipantForm((prev) => ({ ...prev, wa_number: e.target.value }))
                    }
                    placeholder="08xxxxxxxxxx"
                    className="flex h-11 w-full rounded-2xl border border-[#E0E6ED] bg-white px-3.5 py-2 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs"
                  />
                </div>
              </div>

              {dashboard?.config?.is_sparring && (
                <div>
                  <label className="block text-xs font-bold text-[#0E1726] mb-1.5">
                    Tim <span className="text-[#FC480D]">*</span>
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(
                      dashboard?.config?.teams || [
                        { name: 'Tim A', jersey_color: '#FC480D' },
                        { name: 'Tim B', jersey_color: '#3B7DDB' },
                      ]
                    ).map((t) => {
                      const isSelected = addParticipantForm.team === t.name;
                      return (
                        <button
                          key={t.name}
                          type="button"
                          onClick={() => setAddParticipantForm((prev) => ({ ...prev, team: t.name, role: '' }))}
                          className={`h-9 px-4 rounded-full text-xs font-bold transition-all cursor-pointer border ${
                            isSelected
                              ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-2xs'
                              : 'bg-white text-gray-700 border-[#E0E6ED] hover:bg-gray-50'
                          }`}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-[#0E1726] mb-1.5">
                  Posisi{' '}
                  <span className="text-[#FC480D]">*</span>
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  {(
                    dashboard?.config?.player_roles && dashboard.config.player_roles.length > 0
                      ? dashboard.config.player_roles
                      : [
                          { role: 'Kiper', slots: 0 },
                          { role: 'Bek', slots: 0 },
                          { role: 'Gelandang', slots: 0 },
                          { role: 'Penyerang', slots: 0 },
                        ]
                  ).map((pr) => {
                    const rName = typeof pr === 'string' ? pr : pr.role;
                    const isSelected = addParticipantForm.role === rName;

                    let sisaSlot = 0;
                    let isDisabled = false;

                    if (dashboard?.config?.is_sparring) {
                      const selectedTeamName =
                        addParticipantForm.team || dashboard?.config?.teams?.[0]?.name || '';
                      const availability = dashboard?.roles_availability?.find(
                        (ra) => ra.role.toLowerCase() === rName.toLowerCase()
                      );

                      let filledForTeam = 0;
                      if (availability?.team_member_counts && selectedTeamName) {
                        const matchingKey = Object.keys(availability.team_member_counts).find(
                          (k) => k.toLowerCase() === selectedTeamName.toLowerCase()
                        );
                        if (matchingKey) {
                          filledForTeam = availability.team_member_counts[matchingKey];
                        }
                      } else {
                        const isFirstTeam =
                          selectedTeamName?.toLowerCase() ===
                          dashboard?.config?.teams?.[0]?.name?.toLowerCase();

                        filledForTeam = (dashboard?.participants || []).filter((p) => {
                          if (p.status === 'canceled') return false;
                          if (p.role?.toLowerCase() !== rName.toLowerCase()) return false;
                          if (!p.team) return isFirstTeam;
                          return (
                            p.team.trim().toLowerCase() === selectedTeamName?.trim().toLowerCase()
                          );
                        }).length;
                      }

                      const configuredSlots = typeof pr === 'object' ? (pr.slots ?? 0) : 0;
                      sisaSlot = Math.max(0, configuredSlots - filledForTeam);
                      isDisabled = configuredSlots > 0 && sisaSlot === 0;
                    } else {
                      const availability = dashboard?.roles_availability?.find(
                        (ra) => ra.role.toLowerCase() === rName.toLowerCase()
                      );
                      if (availability != null) {
                        sisaSlot = availability.available;
                        isDisabled = availability.available === 0;
                      } else {
                        const configuredSlots = typeof pr === 'object' ? (pr.slots ?? 0) : 0;
                        if (configuredSlots > 0) {
                          const filledCount = (dashboard?.participants || []).filter(
                            (p) => p.status !== 'canceled' && p.role?.toLowerCase() === rName.toLowerCase()
                          ).length;
                          sisaSlot = Math.max(0, configuredSlots - filledCount);
                          isDisabled = sisaSlot === 0;
                        }
                      }
                    }

                    return (
                      <button
                        key={rName}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setAddParticipantForm((prev) => ({ ...prev, role: rName }));
                          }
                        }}
                        className={`h-9 px-4 rounded-full text-xs font-bold transition-all border ${
                          isDisabled
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                            : isSelected
                              ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-2xs scale-102 cursor-pointer'
                              : 'bg-white text-gray-700 border-[#E0E6ED] hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        {rName}
                        {isDisabled ? ' (Penuh)' : sisaSlot > 0 ? ` (${sisaSlot})` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>

              {hasCustomForm ? (
                <div className="pt-4 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={handleCloseAddParticipantModal}
                    className="h-11 px-5 rounded-full border border-gray-200 text-gray-700 font-bold text-xs hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    onClick={handleNextToAddParticipantCustomForm}
                    disabled={
                      !addParticipantForm.name.trim() ||
                      !addParticipantForm.role.trim() ||
                      Boolean(dashboard?.config?.is_sparring && !addParticipantForm.team?.trim())
                    }
                    className="flex-1 h-11 rounded-full bg-[#FC480D] text-white font-bold text-xs hover:bg-[#E8490F] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>Lanjut ke Custom Form</span>
                    <Icon icon="ph:arrow-right-bold" className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleAddParticipantSubmit}
                    disabled={
                      isSubmittingAddParticipant ||
                      !addParticipantForm.name.trim() ||
                      !addParticipantForm.role.trim() ||
                      Boolean(dashboard?.config?.is_sparring && !addParticipantForm.team?.trim())
                    }
                    className="w-full h-11 rounded-full bg-[#FC480D] text-white font-bold text-xs hover:bg-[#E8490F] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmittingAddParticipant ? (
                      <>
                        <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin" />
                        <span>Menambahkan...</span>
                      </>
                    ) : (
                      <span>Tambahkan Pemain</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: CUSTOM FORM */}
          {hasCustomForm && addParticipantTab === 'custom_form' && (
            <div className="space-y-4">
              <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
                {dashboard?.custom_form?.map((field) => {
                  const val = addParticipantCustomAnswers[field.id] || '';
                  const isLevelField =
                    field.template_type === 'level' || field.template === 'level';
                  return (
                    <div key={field.id} className="space-y-1.5">
                      {!isLevelField && (
                        <label className="block text-xs font-bold text-[#0E1726]">
                          {field.title}
                          {field.required ? (
                            <span className="text-[#FC480D] ml-0.5">*</span>
                          ) : (
                            <span className="text-gray-400 font-normal ml-1">(Opsional)</span>
                          )}
                        </label>
                      )}

                      {isLevelField ? (
                        <div className="p-3.5 rounded-2xl bg-white border border-gray-200 space-y-2">
                          <span className="text-sm text-gray-500 block">
                            {field.title}
                            {field.required ? <span className="text-[#FC480D] ml-0.5">*</span> : null}
                          </span>
                          <LevelToggle
                            value={val}
                            options={field.options || []}
                            disabled={isSubmittingAddParticipant}
                            onChange={(next) =>
                              setAddParticipantCustomAnswers((prev) => ({
                                ...prev,
                                [field.id]: next,
                              }))
                            }
                          />
                        </div>
                      ) : field.type === 'option' ? (
                        (field.options || []).length <= 4 ? (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {(field.options || []).map((opt, idx) => {
                              const isSelected = val === opt;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() =>
                                    setAddParticipantCustomAnswers((prev) => ({
                                      ...prev,
                                      [field.id]: isSelected ? '' : opt,
                                    }))
                                  }
                                  className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                                    isSelected
                                      ? 'bg-[#FC480D] border border-[#FC480D] text-white shadow-2xs'
                                      : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  <Icon
                                    icon={isSelected ? 'ph:check-circle-fill' : 'ph:plus-circle-bold'}
                                    className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-gray-400'}`}
                                  />
                                  <span>{opt}</span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="relative">
                            <select
                              value={val}
                              onChange={(e) =>
                                setAddParticipantCustomAnswers((prev) => ({
                                  ...prev,
                                  [field.id]: e.target.value,
                                }))
                              }
                              className="w-full px-3.5 py-2.5 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] focus:outline-none focus:border-[#FC480D] transition-colors appearance-none pr-9 cursor-pointer shadow-2xs"
                            >
                              <option value="">Pilih {field.title}</option>
                              {(field.options || []).map((opt, idx) => (
                                <option key={idx} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            <Icon
                              icon="ph:caret-down-bold"
                              className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                            />
                          </div>
                        )
                      ) : field.type === 'date' ? (
                        <input
                          type="date"
                          value={val}
                          onChange={(e) =>
                            setAddParticipantCustomAnswers((prev) => ({
                              ...prev,
                              [field.id]: e.target.value,
                            }))
                          }
                          className="w-full h-11 px-3.5 py-2 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] focus:outline-none focus:border-[#FC480D] transition-colors shadow-2xs"
                        />
                      ) : field.type === 'number' ? (
                        <input
                          type="number"
                          value={val}
                          placeholder={`Masukkan ${field.title.toLowerCase()}`}
                          onChange={(e) =>
                            setAddParticipantCustomAnswers((prev) => ({
                              ...prev,
                              [field.id]: e.target.value,
                            }))
                          }
                          className="w-full h-11 px-3.5 py-2 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors shadow-2xs"
                        />
                      ) : (
                        <input
                          type="text"
                          value={val}
                          placeholder={`Masukkan ${field.title.toLowerCase()}`}
                          onChange={(e) =>
                            setAddParticipantCustomAnswers((prev) => ({
                              ...prev,
                              [field.id]: e.target.value,
                            }))
                          }
                          className="w-full h-11 px-3.5 py-2 rounded-2xl border border-[#E0E6ED] bg-white text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors shadow-2xs"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setAddParticipantTab('data')}
                  className="h-11 px-5 rounded-full border border-gray-200 text-gray-700 font-bold text-xs hover:bg-gray-50 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <Icon icon="ph:arrow-left-bold" className="w-3.5 h-3.5" />
                  <span>Kembali</span>
                </button>
                <button
                  type="button"
                  onClick={handleAddParticipantSubmit}
                  disabled={isSubmittingAddParticipant}
                  className="flex-1 h-11 rounded-full bg-[#FC480D] text-white font-bold text-xs hover:bg-[#E8490F] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer flex items-center justify-center gap-2"
                >
                  {isSubmittingAddParticipant ? (
                    <>
                      <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin" />
                      <span>Menambahkan...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="ph:check-bold" className="w-4 h-4" />
                      <span>Tambahkan Pemain</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Participant Modal (Edit Member) */}
      <Modal
        isOpen={isEditParticipantModalOpen}
        onClose={handleCloseEditParticipantModal}
        title="Edit Member"
        subtitle="Perbarui data pemain pada event ini."
        size="md"
        position="responsive"
      >
        <div className="space-y-4 pt-1">
          <div>
            <label
              htmlFor="edit-participant-name"
              className="block text-xs font-bold text-[#0E1726] mb-1.5"
            >
              Nama <span className="text-[#FC480D]">*</span>
            </label>
            <input
              id="edit-participant-name"
              type="text"
              value={editParticipantForm.name}
              onChange={(e) => setEditParticipantForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Nama lengkap pemain"
              className="flex h-11 w-full rounded-2xl border border-[#E0E6ED] bg-white px-3.5 py-2 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="edit-participant-email"
                className="block text-xs font-bold text-[#0E1726] mb-1.5"
              >
                Email <span className="text-gray-400 font-normal">(opsional)</span>
              </label>
              <input
                id="edit-participant-email"
                type="email"
                value={editParticipantForm.email}
                onChange={(e) =>
                  setEditParticipantForm((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="nama@email.com"
                className="flex h-11 w-full rounded-2xl border border-[#E0E6ED] bg-white px-3.5 py-2 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs"
              />
            </div>

            <div>
              <label
                htmlFor="edit-participant-wa"
                className="block text-xs font-bold text-[#0E1726] mb-1.5"
              >
                WhatsApp <span className="text-gray-400 font-normal">(opsional)</span>
              </label>
              <input
                id="edit-participant-wa"
                type="text"
                value={editParticipantForm.wa_number}
                onChange={(e) =>
                  setEditParticipantForm((prev) => ({ ...prev, wa_number: e.target.value }))
                }
                placeholder="08xxxxxxxxxx"
                className="flex h-11 w-full rounded-2xl border border-[#E0E6ED] bg-white px-3.5 py-2 text-xs font-medium text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] shadow-2xs"
              />
            </div>
          </div>

          {dashboard?.config?.is_sparring && (
            <div>
              <label className="block text-xs font-bold text-[#0E1726] mb-1.5">
                Tim <span className="text-[#FC480D]">*</span>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {(
                  dashboard?.config?.teams || [
                    { name: 'Tim A', jersey_color: '#FC480D' },
                    { name: 'Tim B', jersey_color: '#3B7DDB' },
                  ]
                ).map((t) => {
                  const isSelected = editParticipantForm.team === t.name;
                  return (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() =>
                        setEditParticipantForm((prev) => ({ ...prev, team: t.name, role: '' }))
                      }
                      className={`h-9 px-4 rounded-full text-xs font-bold transition-all cursor-pointer border ${
                        isSelected
                          ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-2xs'
                          : 'bg-white text-gray-700 border-[#E0E6ED] hover:bg-gray-50'
                      }`}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-[#0E1726] mb-1.5">
              Posisi{' '}
              <span className="text-[#FC480D]">*</span>
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {(
                dashboard?.config?.player_roles && dashboard.config.player_roles.length > 0
                  ? dashboard.config.player_roles
                  : [
                      { role: 'Kiper', slots: 0 },
                      { role: 'Bek', slots: 0 },
                      { role: 'Gelandang', slots: 0 },
                      { role: 'Penyerang', slots: 0 },
                    ]
              ).map((pr) => {
                const rName = typeof pr === 'string' ? pr : pr.role;
                const isSelected = editParticipantForm.role === rName;
                const isCurrentRole = participantToEdit?.role === rName;

                let sisaSlot = 0;
                let isDisabled = false;

                if (dashboard?.config?.is_sparring) {
                  const selectedTeamName =
                    editParticipantForm.team || dashboard?.config?.teams?.[0]?.name || '';
                  const availability = dashboard?.roles_availability?.find(
                    (ra) => ra.role.toLowerCase() === rName.toLowerCase()
                  );

                  let filledForTeam = 0;
                  if (availability?.team_member_counts && selectedTeamName) {
                    const matchingKey = Object.keys(availability.team_member_counts).find(
                      (k) => k.toLowerCase() === selectedTeamName.toLowerCase()
                    );
                    if (matchingKey) {
                      filledForTeam = availability.team_member_counts[matchingKey];
                    }
                    const sameTeam =
                      (participantToEdit?.team || '').trim().toLowerCase() ===
                      selectedTeamName.trim().toLowerCase();
                    if (isCurrentRole && sameTeam) {
                      filledForTeam = Math.max(0, filledForTeam - 1);
                    }
                  } else {
                    const isFirstTeam =
                      selectedTeamName?.toLowerCase() ===
                      dashboard?.config?.teams?.[0]?.name?.toLowerCase();

                    filledForTeam = (dashboard?.participants || []).filter((p) => {
                      if (p.status === 'canceled') return false;
                      if (p.id === participantToEdit?.id) return false;
                      if (p.role?.toLowerCase() !== rName.toLowerCase()) return false;
                      if (!p.team) return isFirstTeam;
                      return (
                        p.team.trim().toLowerCase() === selectedTeamName?.trim().toLowerCase()
                      );
                    }).length;
                  }

                  const configuredSlots = typeof pr === 'object' ? (pr.slots ?? 0) : 0;
                  sisaSlot = Math.max(0, configuredSlots - filledForTeam);
                  isDisabled = !isCurrentRole && configuredSlots > 0 && sisaSlot === 0;
                } else {
                  const availability = dashboard?.roles_availability?.find(
                    (ra) => ra.role.toLowerCase() === rName.toLowerCase()
                  );
                  if (availability != null) {
                    sisaSlot = isCurrentRole
                      ? availability.available + 1
                      : availability.available;
                    isDisabled = !isCurrentRole && availability.available === 0;
                  } else {
                    const configuredSlots = typeof pr === 'object' ? (pr.slots ?? 0) : 0;
                    if (configuredSlots > 0) {
                      const filledCount = (dashboard?.participants || []).filter(
                        (p) =>
                          p.status !== 'canceled' &&
                          p.id !== participantToEdit?.id &&
                          p.role?.toLowerCase() === rName.toLowerCase()
                      ).length;
                      sisaSlot = Math.max(0, configuredSlots - filledCount);
                      isDisabled = !isCurrentRole && sisaSlot === 0;
                    }
                  }
                }

                return (
                  <button
                    key={rName}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) {
                        setEditParticipantForm((prev) => ({ ...prev, role: rName }));
                      }
                    }}
                    className={`h-9 px-4 rounded-full text-xs font-bold transition-all border ${
                      isDisabled
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                        : isSelected
                          ? 'bg-[#FC480D] text-white border-[#FC480D] shadow-2xs scale-102 cursor-pointer'
                          : 'bg-white text-gray-700 border-[#E0E6ED] hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    {rName}
                    {isDisabled ? ' (Penuh)' : sisaSlot > 0 ? ` (${sisaSlot})` : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleCloseEditParticipantModal}
              className="h-11 px-5 rounded-full border border-gray-200 text-gray-700 font-bold text-xs hover:bg-gray-50 transition-all cursor-pointer"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleEditParticipantSubmit}
              disabled={
                isSubmittingEditParticipant ||
                !editParticipantForm.name.trim() ||
                !editParticipantForm.role.trim() ||
                Boolean(dashboard?.config?.is_sparring && !editParticipantForm.team?.trim())
              }
              className="flex-1 h-11 rounded-full bg-[#FC480D] text-white font-bold text-xs hover:bg-[#E8490F] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer flex items-center justify-center gap-2"
            >
              {isSubmittingEditParticipant ? (
                <>
                  <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <span>Simpan Perubahan</span>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Payment Status Change Modal */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title="Konfirmasi Ubah Status"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin ingin mengubah status pembayaran{' '}
            <strong>{participantToUpdate?.name}</strong> menjadi{' '}
            <strong className={newPaymentStatus === 'paid' ? 'text-green-600' : 'text-orange-600'}>
              {newPaymentStatus === 'paid' ? 'PAID' : 'UNPAID'}
            </strong>
            ?
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsStatusModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmPaymentStatusChange}
              loading={isUpdatingStatus}
            >
              Ya, Ubah
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isStatusMembershipModalOpen}
        onClose={() => {
          setIsStatusMembershipModalOpen(false);
          setMembershipStatusError(null);
        }}
        title="Konfirmasi Ubah Status Membership"
        size="sm"
      >
        <div className="space-y-4">
          {membershipStatusError && (
            <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
              <Icon icon="ph:warning-circle-bold" className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <span className="leading-snug">{membershipStatusError}</span>
            </div>
          )}

          <p className="text-gray-600">
            Apakah Anda yakin ingin mengubah status membership{' '}
            <strong>{participantMembershipToUpdate?.name}</strong> menjadi{' '}
            <strong className={'text-green-600'}>{newMembershipStatus.toUpperCase()}</strong>?
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsStatusMembershipModalOpen(false);
                setMembershipStatusError(null);
              }}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmMembershipStatusChange}
              loading={isUpdatingStatus}
            >
              Ya, Ubah
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Payment Reminder Modal */}
      <Modal
        isOpen={isPaymentReminderBulkModalOpen}
        onClose={() => setIsPaymentReminderBulkModalOpen(false)}
        title="Kirim Reminder Pembayaran"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Apakah Anda yakin mengirim pengingat pembayaran ke{' '}
            <strong className="text-orange-600">{unpaidCount} pemain</strong>?
          </p>
          <p className="text-sm text-gray-500">
            Email reminder hanya akan dikirim sekali ke setiap pemain yang belum membayar dan belum
            pernah menerima reminder.
          </p>

          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPaymentReminderBulkModalOpen(false)}
              borderClass="border-gray-300"
              textClass="text-gray-700"
              hoverBgClass="hover:bg-gray-50"
              hoverBorderClass="hover:border-gray-400"
            >
              Batal
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSendBulkPaymentReminder}
              loading={isSendingReminder}
              borderClass="border-orange-500"
              textClass="text-orange-500"
              hoverBorderClass="hover:border-orange-600"
              hoverBgClass="hover:bg-orange-50"
            >
              <Icon icon="mdi:bell-ring-outline" className="w-4 h-4 mr-2" />
              Ya, Kirim Reminder
            </Button>
          </div>
        </div>
      </Modal>

      {/* 1. Mobile Member Actions Bottom Modal (Mockup 1) */}
      <Modal
        isOpen={isMobileActionsModalOpen}
        onClose={() => setIsMobileActionsModalOpen(false)}
        size="md"
        position="bottom"
        hideCloseButton={true}
      >
        {selectedMobileParticipant && (() => {
          const isPaid = selectedMobileParticipant.payment_status === 'paid';
          const isQrisPaid = isPaid && selectedMobileParticipant.payment_method?.toLowerCase() === 'qris';
          const isPaidEvent = dashboard?.type === 'paid';
          const canPayQris = isPaidEvent && !isPaid;
          const canSendEmailReminder =
            isPaidEvent && !isPaid && hasDeliverableEmail(selectedMobileParticipant.email);

          return (
            <div className="space-y-4 pb-2">
              {/* Member Header (Flex row with Avatar, Info, and Close Button) */}
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-100">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-12 h-12 rounded-full bg-[#FFF5EE] text-[#FC480D] font-extrabold text-base flex items-center justify-center shrink-0 border border-[#FBD9C7]">
                    {selectedMobileParticipant.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-extrabold text-base text-[#0E1726] truncate">
                      {selectedMobileParticipant.name}
                    </h3>
                    <p className="text-xs text-gray-500 font-medium truncate">
                      {selectedMobileParticipant.team ? `${selectedMobileParticipant.team} · ` : ''}
                      {isPaid ? 'Sudah bayar' : 'Belum bayar'} · {isPaidEvent ? formatCurrency(selectedMobileParticipant.price) : 'Free'}
                    </p>
                  </div>
                </div>

                {/* Close Button in same row */}
                <button
                  type="button"
                  onClick={() => setIsMobileActionsModalOpen(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
                  aria-label="Close modal"
                >
                  <Icon icon="ph:x-bold" className="w-4 h-4" />
                </button>
              </div>

              {/* Action List Items */}
              <div className="space-y-1">
                {/* Option 1: Ubah Status Pembayaran (Disembunyikan jika LUNAS & QRIS) */}
                {!isQrisPaid && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileActionsModalOpen(false);
                      setIsMobilePaymentStatusModalOpen(true);
                    }}
                    className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-gray-50 transition-colors text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:wallet-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                          Ubah Status Pembayaran
                        </h4>
                        <p className="text-xs text-gray-400 font-medium">Tandai sudah bayar</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </button>
                )}

                {/* Option 1b: Ubah Status Member (Selalu Muncul) */}
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileActionsModalOpen(false);
                    setIsMobileMembershipStatusModalOpen(true);
                  }}
                  className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-gray-50 transition-colors text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                      <Icon icon="ph:user-gear-bold" className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                        Ubah Status Member
                      </h4>
                      <p className="text-xs text-gray-400 font-medium">Konfirmasi atau tolak keikutsertaan</p>
                    </div>
                  </div>
                  <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                </button>

                {/* Option 1c: Lihat Jawaban Formulir */}
                {(Boolean(selectedMobileParticipant.custom_form_answer?.length) ||
                  Boolean(dashboard?.custom_form?.length)) && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileActionsModalOpen(false);
                      setViewingFormAnswersParticipant(selectedMobileParticipant);
                    }}
                    className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-gray-50 transition-colors text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:clipboard-text-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                          Lihat Jawaban Formulir
                        </h4>
                        <p className="text-xs text-gray-400 font-medium">Informasi tambahan pendaftaran</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </button>
                )}

                {/* Option 2: Reminder email (hanya email valid; guest/manual tanpa email memakai QRIS) */}
                {canSendEmailReminder && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileActionsModalOpen(false);
                      handleOpenSinglePaymentReminderModal(selectedMobileParticipant);
                    }}
                    className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-gray-50 transition-colors text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:bell-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                          Reminder Pembayaran
                        </h4>
                        <p className="text-xs text-gray-400 font-medium">Kirim pengingat ke email peserta</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </button>
                )}

                {/* Option 3: Tunjukkan Kode Bayar (Hanya jika event berbayar dan belum lunas) */}
                {canPayQris && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileActionsModalOpen(false);
                      handleOpenQrisPayment(selectedMobileParticipant);
                    }}
                    className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-gray-50 transition-colors text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:qr-code-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                          Tunjukkan Kode Bayar
                        </h4>
                        <p className="text-xs text-gray-400 font-medium">QRIS & kode virtual account</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </button>
                )}

                {/* Option 3b: Edit Member (kecuali canceled) */}
                {selectedMobileParticipant.status !== 'canceled' && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileActionsModalOpen(false);
                      handleOpenEditParticipantModal(selectedMobileParticipant);
                    }}
                    className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-gray-50 transition-colors text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                        <Icon icon="ph:user-circle-gear-bold" className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-[#0E1726] group-hover:text-[#FC480D] transition-colors">
                          Edit Member
                        </h4>
                        <p className="text-xs text-gray-400 font-medium">Ubah nama, email, WA, posisi</p>
                      </div>
                    </div>
                    <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                  </button>
                )}

                {/* Option 4: Hapus dari Event (Selalu Muncul) */}
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileActionsModalOpen(false);
                    handleDeleteClick(selectedMobileParticipant);
                  }}
                  className="w-full flex items-center justify-between py-3 rounded-2xl hover:bg-red-50/60 transition-colors text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                      <Icon icon="ph:trash-bold" className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-red-500">
                        Hapus dari Event
                      </h4>
                      <p className="text-xs text-gray-400 font-medium">Slot akan kembali tersedia</p>
                    </div>
                  </div>
                  <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-red-400" />
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* 2. Mobile Status Pembayaran Bottom Modal (Mockup 2) */}
      <Modal
        isOpen={isMobilePaymentStatusModalOpen}
        onClose={() => setIsMobilePaymentStatusModalOpen(false)}
        title="Status Pembayaran"
        subtitle={
          selectedMobileParticipant
            ? `${selectedMobileParticipant.name} · ${selectedMobileParticipant.team || 'Pemain'} · ${formatCurrency(selectedMobileParticipant.price)}`
            : ''
        }
        size="md"
        position="bottom"
      >
        {selectedMobileParticipant && (
          <div className="space-y-3 pt-1 pb-2">
            {/* Paid Card */}
            <button
              type="button"
              onClick={async () => {
                setIsMobilePaymentStatusModalOpen(false);
                handlePaymentStatusClick(selectedMobileParticipant, 'paid');
              }}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                selectedMobileParticipant.payment_status === 'paid'
                  ? 'border-2 border-[#FC480D] bg-white shadow-2xs'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-[#0A9E3C] flex items-center justify-center shrink-0">
                  <Icon icon="ph:check-circle-fill" className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#0E1726]">Paid</h4>
                  <p className="text-xs text-gray-400 font-medium">Peserta sudah membayar penuh</p>
                </div>
              </div>
              {selectedMobileParticipant.payment_status === 'paid' ? (
                <Icon icon="ph:check-circle-fill" className="w-6 h-6 text-[#FC480D]" />
              ) : (
                <Icon icon="ph:circle-bold" className="w-6 h-6 text-gray-300" />
              )}
            </button>

            {/* Unpaid Card */}
            <button
              type="button"
              onClick={async () => {
                setIsMobilePaymentStatusModalOpen(false);
                handlePaymentStatusClick(selectedMobileParticipant, 'unpaid');
              }}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                selectedMobileParticipant.payment_status === 'unpaid'
                  ? 'border-2 border-[#FC480D] bg-white shadow-2xs'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <Icon icon="ph:clock-fill" className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#0E1726]">Unpaid</h4>
                  <p className="text-xs text-gray-400 font-medium">Pembayaran belum diterima</p>
                </div>
              </div>
              {selectedMobileParticipant.payment_status === 'unpaid' ? (
                <Icon icon="ph:check-circle-fill" className="w-6 h-6 text-[#FC480D]" />
              ) : (
                <Icon icon="ph:circle-bold" className="w-6 h-6 text-gray-300" />
              )}
            </button>
          </div>
        )}
      </Modal>

      {/* 2b. Mobile Status Keanggotaan Bottom Modal */}
      <Modal
        isOpen={isMobileMembershipStatusModalOpen}
        onClose={() => setIsMobileMembershipStatusModalOpen(false)}
        title="Status Keanggotaan"
        subtitle={
          selectedMobileParticipant
            ? `${selectedMobileParticipant.name} · ${selectedMobileParticipant.team || 'Pemain'}`
            : ''
        }
        size="md"
        position="bottom"
      >
        {selectedMobileParticipant && (
          <div className="space-y-3 pt-1 pb-2">
            {/* Confirmed Card */}
            <button
              type="button"
              onClick={() => {
                setIsMobileMembershipStatusModalOpen(false);
                handleMembershipStatusClick(selectedMobileParticipant, 'confirmed');
              }}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                selectedMobileParticipant.status === 'confirmed'
                  ? 'border-2 border-[#FC480D] bg-white shadow-2xs'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-[#0A9E3C] flex items-center justify-center shrink-0">
                  <Icon icon="ph:check-circle-fill" className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#0E1726]">Konfirmasi (Confirmed)</h4>
                  <p className="text-xs text-gray-400 font-medium">Peserta terkonfirmasi mengikuti event</p>
                </div>
              </div>
              {selectedMobileParticipant.status === 'confirmed' ? (
                <Icon icon="ph:check-circle-fill" className="w-6 h-6 text-[#FC480D]" />
              ) : (
                <Icon icon="ph:circle-bold" className="w-6 h-6 text-gray-300" />
              )}
            </button>

            {/* Pending Card */}
            <button
              type="button"
              onClick={() => {
                setIsMobileMembershipStatusModalOpen(false);
                handleMembershipStatusClick(selectedMobileParticipant, 'pending');
              }}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                selectedMobileParticipant.status === 'pending'
                  ? 'border-2 border-[#FC480D] bg-white shadow-2xs'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                  <Icon icon="ph:clock-fill" className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#0E1726]">Pending</h4>
                  <p className="text-xs text-gray-400 font-medium">Menunggu konfirmasi keikutsertaan</p>
                </div>
              </div>
              {selectedMobileParticipant.status === 'pending' ? (
                <Icon icon="ph:check-circle-fill" className="w-6 h-6 text-[#FC480D]" />
              ) : (
                <Icon icon="ph:circle-bold" className="w-6 h-6 text-gray-300" />
              )}
            </button>

            {/* Canceled Card */}
            <button
              type="button"
              onClick={() => {
                setIsMobileMembershipStatusModalOpen(false);
                handleMembershipStatusClick(selectedMobileParticipant, 'canceled');
              }}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left cursor-pointer ${
                selectedMobileParticipant.status === 'canceled'
                  ? 'border-2 border-[#FC480D] bg-white shadow-2xs'
                  : 'border border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                  <Icon icon="ph:x-circle-fill" className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#0E1726]">Tolak / Batal (Canceled)</h4>
                  <p className="text-xs text-gray-400 font-medium">Peserta ditolak atau membatalkan keikutsertaan</p>
                </div>
              </div>
              {selectedMobileParticipant.status === 'canceled' ? (
                <Icon icon="ph:check-circle-fill" className="w-6 h-6 text-[#FC480D]" />
              ) : (
                <Icon icon="ph:circle-bold" className="w-6 h-6 text-gray-300" />
              )}
            </button>
          </div>
        )}
      </Modal>

      {/* 3. Single Payment Reminder Alert Modal (Mockup 3) */}
      <Modal
        isOpen={isPaymentReminderSingleModalOpen}
        onClose={() => {
          setIsPaymentReminderSingleModalOpen(false);
          setParticipantForReminder(null);
        }}
        size="sm"
        position="responsive"
      >
        <div className="p-3 text-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-[#FFF5EE] text-[#FC480D] flex items-center justify-center mx-auto mb-2">
            <Icon icon="ph:bell-fill" className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-extrabold text-[#0E1726]">
            Reminder Pembayaran
          </h3>
          <p className="text-xs text-gray-500 font-medium leading-relaxed max-w-xs mx-auto">
            Pengingat pembayaran {formatCurrency(participantForReminder?.price || 75000)} dikirim ke{' '}
            <span className="font-semibold text-gray-700">{participantForReminder?.wa_number || participantForReminder?.email || 'peserta'}</span>.
          </p>

          <div className="pt-2">
            <button
              type="button"
              onClick={handleSendSinglePaymentReminder}
              disabled={isSendingReminder}
              className="w-full h-11 rounded-full bg-[#FC480D] text-white font-bold text-sm hover:bg-[#E8490F] disabled:opacity-50 transition-colors cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
            >
              {isSendingReminder ? (
                <>
                  <Icon icon="ph:spinner-gap-bold" className="w-4 h-4 animate-spin" />
                  <span>Mengirim...</span>
                </>
              ) : (
                <span>Mengerti</span>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {notification.isOpen && (
        <Notification
          isOpen={notification.isOpen}
          onClose={() => setNotification({ isOpen: false, message: '', type: 'success' })}
          message={notification.message}
          variant={notification.type}
        />
      )}

      {/* View Custom Form Answers Modal */}
      <CustomFormAnswersViewModal
        isOpen={Boolean(viewingFormAnswersParticipant)}
        onClose={() => setViewingFormAnswersParticipant(null)}
        participantName={viewingFormAnswersParticipant?.name || ''}
        answers={viewingFormAnswersParticipant?.custom_form_answer}
        customFormQuestions={dashboard?.custom_form}
        editableLevel={
          isBadmintonLevelEvent && viewingFormAnswersParticipant
            ? {
                value: readParticipantLevel({
                  role: viewingFormAnswersParticipant.role,
                  answers: viewingFormAnswersParticipant.custom_form_answer,
                }),
                options: CANONICAL_BADMINTON_LEVELS,
                saving: isUpdatingStatus,
                onSave: (level) =>
                  handleLevelChange(viewingFormAnswersParticipant, level),
              }
            : undefined
        }
      />

      <AlertDialog
        open={reviewPopupShow}
        onOpenChange={(open) => {
          if (!open) {
            setReviewPopupShow(false);
            setReviewParticipant(null);
            setParticipantToDelete(null);
          }
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon icon="mdi:logout" className="w-8 h-8 text-red-500" />
            </div>
            <AlertDialogTitle className="text-center text-xl">
              Permintaan Pembatalan Player
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-2">
              <code>
                Nama: {reviewParticipant?.name}
                <br />
                Event: {dashboard.name}
              </code>
              <p className="mt-4 border-t pt-4">
                Player mengajukan permintaan untuk keluar dari event ini. Silakan pilih tindakan
                yang ingin kamu lakukan.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 sm:gap-3">
            <AlertDialogCancel
              disabled={loading}
              className="flex-1"
            >
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={loading}
              className="flex-1 bg-red-500 hover:bg-red-600"
            >
              {loading ? (
                <>
                  <Icon icon="mdi:loading" className="w-5 h-5 animate-spin mr-2" />
                  Memproses...
                </>
              ) : (
                'Setujui Pembatalan'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MabarDashboardPage;
